import { showToast, formatError } from "../lib/dom";
import { trackEvent } from "../lib/analytics";
import { fetchWebClothesList } from "../lib/remoteConfig";
import {
  resolveStoragePathToUrl,
  pathToDataUrl,
} from "../lib/firebaseStorage";
import type { LibraryEntry } from "../lib/state";
import type {
  ClothesCategory,
  ClothesItem,
  ClothesResponse,
  Gender,
} from "../types/clothes";

const GENDER_STORAGE_KEY = "acc_gender_v1";

type CategoryFilter = ClothesCategory | "all";

interface ModalState {
  overlay: HTMLElement;
  dialog: HTMLElement;
  grid: HTMLElement;
  searchInput: HTMLInputElement;
  countLabel: HTMLElement;
  confirmBtn: HTMLButtonElement;
  observer: IntersectionObserver;
  cleanup: Array<() => void>;
  closed: boolean;
  confirmed: boolean;
  onComplete: (entries: LibraryEntry[]) => void;
  data: ClothesResponse | null;
  gender: Gender;
  category: CategoryFilter;
  search: string;
  selected: Map<string, ClothesItem>;
}

let active: ModalState | null = null;

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function readPersistedGender(): Gender {
  try {
    const value = localStorage.getItem(GENDER_STORAGE_KEY);
    if (value === "male" || value === "female") return value;
  } catch {
    // Best effort.
  }
  return "female";
}

function persistGender(gender: Gender): void {
  try {
    localStorage.setItem(GENDER_STORAGE_KEY, gender);
  } catch {
    // Best effort.
  }
}

function buildModal(): HTMLElement {
  const overlay = document.createElement("div");
  overlay.className = "catalog-overlay";
  overlay.innerHTML = `
    <div
      class="catalog-dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="catalog-title"
    >
      <header class="catalog-header">
        <div>
          <p class="catalog-eyebrow">Collection</p>
          <h2 id="catalog-title">Browse our collection</h2>
        </div>
        <button class="catalog-close" type="button" aria-label="Close">
          <span aria-hidden="true">&times;</span>
        </button>
      </header>

      <div class="catalog-filters">
        <div class="catalog-filter-row">
          <div class="catalog-segment" role="tablist" aria-label="Gender">
            <button class="catalog-segment-btn" type="button" data-gender="female" role="tab">Women</button>
            <button class="catalog-segment-btn" type="button" data-gender="male" role="tab">Men</button>
          </div>
          <div class="catalog-chip-row" role="tablist" aria-label="Category">
            <button class="catalog-chip" type="button" data-category="all" role="tab">All</button>
            <button class="catalog-chip" type="button" data-category="fullBody" role="tab">Full Body</button>
            <button class="catalog-chip" type="button" data-category="upper" role="tab">Top</button>
            <button class="catalog-chip" type="button" data-category="lower" role="tab">Bottom</button>
          </div>
        </div>
        <label class="catalog-search">
          <span class="visually-hidden">Search clothes</span>
          <input
            type="search"
            class="catalog-search-input"
            placeholder="Search by name, color, or type"
            autocomplete="off"
          />
        </label>
      </div>

      <div class="catalog-grid-wrap">
        <div
          class="catalog-grid"
          role="listbox"
          aria-multiselectable="true"
          aria-label="Clothes"
        ></div>
        <div class="catalog-status" hidden></div>
      </div>

      <footer class="catalog-footer">
        <span class="catalog-count" aria-live="polite">Selected 0 / 2</span>
        <div class="catalog-footer-actions">
          <button class="button button-secondary catalog-cancel" type="button">Cancel</button>
          <button class="button button-primary catalog-confirm" type="button" disabled>Add to outfit</button>
        </div>
      </footer>
    </div>
  `;
  return overlay;
}

function createCard(item: ClothesItem, isSelected: boolean): HTMLButtonElement {
  const card = document.createElement("button");
  card.type = "button";
  card.className = `catalog-card${isSelected ? " is-selected" : ""}`;
  card.setAttribute("role", "option");
  card.setAttribute("aria-selected", isSelected ? "true" : "false");
  card.dataset.itemId = item.id;
  card.dataset.storagePath = item.imageReference;
  card.innerHTML = `
    <div class="catalog-card-image">
      <span class="catalog-card-check" aria-hidden="true">✓</span>
      <img
        loading="lazy"
        decoding="async"
        width="200"
        height="240"
        alt="${escapeHtml(item.name)}"
      />
    </div>
    <div class="catalog-card-meta">
      <strong class="catalog-card-title">${escapeHtml(item.name)}</strong>
      <span class="catalog-card-tag">${escapeHtml(item.colorTag)}</span>
    </div>
  `;
  return card;
}

function applyFilters(): void {
  if (!active || !active.data) return;
  const items = active.data[active.gender] ?? [];
  const search = active.search.trim().toLowerCase();
  const filtered = items.filter(item => {
    if (active!.category !== "all" && item.type.category !== active!.category) return false;
    if (search) {
      const haystack =
        `${item.name} ${item.colorTag} ${item.type.value}`.toLowerCase();
      if (!haystack.includes(search)) return false;
    }
    return true;
  });
  renderGrid(filtered);
}

function renderGrid(items: ClothesItem[]): void {
  if (!active) return;
  active.observer.disconnect();
  active.grid.innerHTML = "";

  if (!items.length) {
    const empty = document.createElement("div");
    empty.className = "catalog-empty";
    empty.textContent = "No clothes match these filters.";
    active.grid.appendChild(empty);
    return;
  }

  const fragment = document.createDocumentFragment();
  items.forEach(item => {
    const isSelected = active!.selected.has(item.id);
    const card = createCard(item, isSelected);
    card.addEventListener("click", () => toggleSelection(item, card));
    fragment.appendChild(card);
  });
  active.grid.appendChild(fragment);

  active.grid.querySelectorAll<HTMLElement>(".catalog-card").forEach(card => {
    active!.observer.observe(card);
  });
}

function toggleSelection(item: ClothesItem, card: HTMLElement): void {
  if (!active) return;

  if (active.selected.has(item.id)) {
    active.selected.delete(item.id);
    card.classList.remove("is-selected");
    card.setAttribute("aria-selected", "false");
  } else {
    if (active.selected.size >= 2) {
      const firstKey = active.selected.keys().next().value;
      if (firstKey) {
        active.selected.delete(firstKey);
        const previousCard = active.grid.querySelector<HTMLElement>(
          `.catalog-card[data-item-id="${firstKey}"]`,
        );
        previousCard?.classList.remove("is-selected");
        previousCard?.setAttribute("aria-selected", "false");
      }
    }
    active.selected.set(item.id, item);
    card.classList.add("is-selected");
    card.setAttribute("aria-selected", "true");
    trackEvent("clothes_catalog_item_selected", {
      clothing_id: item.id,
      total_selected: active.selected.size,
    });
  }

  updateFooter();
}

function updateFooter(): void {
  if (!active) return;
  const count = active.selected.size;
  active.countLabel.textContent = `Selected ${count} / 2`;
  active.confirmBtn.disabled = count === 0;
}

function syncSegmentButtons(): void {
  if (!active) return;
  active.dialog.querySelectorAll<HTMLButtonElement>(".catalog-segment-btn").forEach(button => {
    const matches = button.dataset.gender === active!.gender;
    button.classList.toggle("is-active", matches);
    button.setAttribute("aria-selected", matches ? "true" : "false");
  });
  active.dialog.querySelectorAll<HTMLButtonElement>(".catalog-chip").forEach(chip => {
    const matches = chip.dataset.category === active!.category;
    chip.classList.toggle("is-active", matches);
    chip.setAttribute("aria-selected", matches ? "true" : "false");
  });
}

function setStatus(message: string | null, isError = false): void {
  if (!active) return;
  const status = active.dialog.querySelector<HTMLElement>(".catalog-status");
  if (!status) return;
  if (!message) {
    status.hidden = true;
    status.textContent = "";
    return;
  }
  status.hidden = false;
  status.textContent = message;
  status.classList.toggle("is-error", isError);
}

function lockBodyScroll(): () => void {
  const previous = document.body.style.overflow;
  document.body.style.overflow = "hidden";
  return () => {
    document.body.style.overflow = previous;
  };
}

function trapFocus(dialog: HTMLElement): () => void {
  function handle(event: KeyboardEvent): void {
    if (event.key !== "Tab") return;
    const focusable = dialog.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])',
    );
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (!first || !last) return;

    const activeElement = document.activeElement as HTMLElement | null;
    if (event.shiftKey && activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }
  dialog.addEventListener("keydown", handle);
  return () => dialog.removeEventListener("keydown", handle);
}

export function openClothesCatalog(
  onComplete: (entries: LibraryEntry[]) => void,
): void {
  if (active) return;

  trackEvent("clothes_catalog_opened");

  const overlay = buildModal();
  document.body.appendChild(overlay);

  const dialog = overlay.querySelector<HTMLElement>(".catalog-dialog");
  const grid = overlay.querySelector<HTMLElement>(".catalog-grid");
  const searchInput = overlay.querySelector<HTMLInputElement>(".catalog-search-input");
  const countLabel = overlay.querySelector<HTMLElement>(".catalog-count");
  const confirmBtn = overlay.querySelector<HTMLButtonElement>(".catalog-confirm");
  if (!dialog || !grid || !searchInput || !countLabel || !confirmBtn) {
    overlay.remove();
    return;
  }

  const observer = new IntersectionObserver(
    entries => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        const card = entry.target as HTMLElement;
        const path = card.dataset.storagePath;
        const img = card.querySelector<HTMLImageElement>("img");
        if (!path || !img || img.src) return;

        resolveStoragePathToUrl(path)
          .then(url => {
            if (!active || active.closed) return;
            if (!img.src) img.src = url;
          })
          .catch(error => {
            console.error("Failed to resolve image", path, error);
          });

        observer.unobserve(card);
      });
    },
    { rootMargin: "240px" },
  );

  active = {
    overlay,
    dialog,
    grid,
    searchInput,
    countLabel,
    confirmBtn,
    observer,
    cleanup: [],
    closed: false,
    confirmed: false,
    onComplete,
    data: null,
    gender: readPersistedGender(),
    category: "all",
    search: "",
    selected: new Map<string, ClothesItem>(),
  };

  active.cleanup.push(() => observer.disconnect());
  active.cleanup.push(lockBodyScroll());
  active.cleanup.push(trapFocus(dialog));

  function handleEscape(event: KeyboardEvent): void {
    if (event.key === "Escape") closeClothesCatalog();
  }
  document.addEventListener("keydown", handleEscape);
  active.cleanup.push(() => document.removeEventListener("keydown", handleEscape));

  overlay.addEventListener("click", event => {
    if (event.target === overlay) closeClothesCatalog();
  });

  dialog.querySelector(".catalog-close")?.addEventListener("click", () => {
    closeClothesCatalog();
  });
  dialog.querySelector(".catalog-cancel")?.addEventListener("click", () => {
    closeClothesCatalog();
  });
  confirmBtn.addEventListener("click", () => {
    void handleConfirm();
  });

  dialog.querySelectorAll<HTMLButtonElement>(".catalog-segment-btn").forEach(button => {
    button.addEventListener("click", () => {
      const next = button.dataset.gender as Gender | undefined;
      if (!next || !active || active.gender === next) return;
      active.gender = next;
      persistGender(next);
      trackEvent("clothes_catalog_filtered", {
        gender: active.gender,
        category: active.category,
        has_search: Boolean(active.search.trim()),
      });
      syncSegmentButtons();
      applyFilters();
    });
  });

  dialog.querySelectorAll<HTMLButtonElement>(".catalog-chip").forEach(chip => {
    chip.addEventListener("click", () => {
      const next = (chip.dataset.category ?? "all") as CategoryFilter;
      if (!active || active.category === next) return;
      active.category = next;
      trackEvent("clothes_catalog_filtered", {
        gender: active.gender,
        category: active.category,
        has_search: Boolean(active.search.trim()),
      });
      syncSegmentButtons();
      applyFilters();
    });
  });

  let searchDebounce: number | undefined;
  searchInput.addEventListener("input", () => {
    if (!active) return;
    if (searchDebounce !== undefined) window.clearTimeout(searchDebounce);
    searchDebounce = window.setTimeout(() => {
      if (!active) return;
      active.search = searchInput.value;
      applyFilters();
    }, 120);
  });
  active.cleanup.push(() => {
    if (searchDebounce !== undefined) window.clearTimeout(searchDebounce);
  });

  syncSegmentButtons();
  setStatus("Loading…");
  searchInput.focus();

  fetchWebClothesList()
    .then(data => {
      if (!active || active.closed) return;
      active.data = data;
      const totalForGender =
        (data[active.gender]?.length ?? 0) +
        (data[active.gender === "female" ? "male" : "female"]?.length ?? 0);
      if (totalForGender === 0) {
        setStatus("The collection is empty right now. Please try again later.");
        return;
      }
      setStatus(null);
      applyFilters();
    })
    .catch(error => {
      console.error(error);
      setStatus("Couldn't load the collection. Please try again.", true);
    });
}

export function closeClothesCatalog(): void {
  if (!active) return;
  active.closed = true;
  if (!active.confirmed) {
    trackEvent("clothes_catalog_dismissed", {
      total_selected: active.selected.size,
    });
  }
  active.cleanup.forEach(fn => {
    try {
      fn();
    } catch (error) {
      console.error("Catalog cleanup failed:", error);
    }
  });
  active.overlay.remove();
  active = null;
}

async function handleConfirm(): Promise<void> {
  if (!active || active.selected.size === 0) return;

  const items = [...active.selected.values()];
  const onComplete = active.onComplete;
  const confirmBtn = active.confirmBtn;

  confirmBtn.disabled = true;
  const originalLabel = confirmBtn.textContent;
  confirmBtn.textContent = "Adding…";

  try {
    const entries: LibraryEntry[] = [];
    for (const item of items) {
      const dataUrl = await pathToDataUrl(item.imageReference);
      entries.push({
        id: item.id,
        name: item.name,
        dataUrl,
        source: "catalog",
      });
    }

    trackEvent("clothes_catalog_confirmed", { total_selected: entries.length });
    if (active) active.confirmed = true;
    closeClothesCatalog();
    onComplete(entries);
    showToast(
      entries.length > 1
        ? "Added garments to your outfit."
        : "Added garment to your outfit.",
    );
  } catch (error: unknown) {
    if (active) {
      active.confirmBtn.disabled = false;
      active.confirmBtn.textContent = originalLabel ?? "Add to outfit";
    }
    showToast(formatError(error), true);
  }
}
